import {
  IOverlayStyleProps,
  IOverlayStyles,
  IPanelStyles,
  IStyleFunctionOrObject,
  memoizeFunction,
  mergeStyleSets,
  useTheme,
} from '@fluentui/react';
import { getDefaultScrollbarColorSchemeStyle, ThemeType } from 'common/theme';
import { IAppTheme, useAppTheme, useCurrentThemeType } from 'common/theme/AppTheme';
import { pageDataService } from 'common/utilities';
import { mergeWith } from 'lodash';

import { usePanelMobileView } from './usePanelMobileView';

const getPanelBackground = (theme: IAppTheme, isWebView: boolean, isMobileView: boolean, bgColorOverride?: string) => {
  if (isWebView) {
    return 'transparent';
  }

  if (bgColorOverride) {
    return bgColorOverride;
  }

  if (isMobileView) {
    return theme.appColors?.pageBackground;
  }
  return theme.palette.white;
};

const getPanelStyles = memoizeFunction(
  (
    theme: IAppTheme,
    themeName: ThemeType,
    isWebView: boolean,
    isMobileView: boolean,
    customStyles?: Partial<IPanelStyles>,
    bgColorOverride?: string
  ): Partial<IPanelStyles> => {
    return mergeWith(
      {},
      {
        root: {
          top: isWebView ? 0 : 48,
          ...getDefaultScrollbarColorSchemeStyle(themeName),
        },
        main: {
          background: getPanelBackground(theme, isWebView, isMobileView, bgColorOverride),
          ...(isMobileView && {
            boxShadow: 'none',
          }),
        },
        commands: {
          background: getPanelBackground(theme, isWebView, isMobileView, bgColorOverride),
          zIndex: 100, // to make sure the commands bar is on top of the content,
          position: 'static !important' as string,
          paddingTop: 0,
        },
        footer: {
          background: getPanelBackground(theme, isWebView, isMobileView, bgColorOverride),
        },
        closeButton: { marginRight: 0, width: 40, height: 40 },
        header: {
          paddingTop: 56,
        },
        headerText: { ...theme.appFonts?.Title1Strong },
        content: {
          padding: isMobileView ? '40px 0 24px 0' : '32px 24px 24px 24px',
        },
      } as Partial<IPanelStyles>,
      customStyles || {}
    );
  }
);

const getStyles = memoizeFunction((isMobileView: boolean, isWebView: boolean) => {
  return mergeStyleSets({
    headerStack: {
      alignSelf: 'flex-start',
      flexGrow: 1,
      // client requests 0 top padding
      padding: isWebView ? '0 24px' : '40px 24px 0 24px',
    },
    headerText: {
      // client requests 0 top padding
      paddingTop: isMobileView || isWebView ? 0 : 16,
    },
    mobileHeaderGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      gridColumnGap: 16,
      justifyItems: 'start',
      alignItems: 'center',
      padding: '16px 24px 0 24px',
    },
    backButton: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: isMobileView ? 42 : 32,
      width: isMobileView ? 42 : undefined,
      alignItems: 'center',
      ...(isMobileView
        ? {
            borderRadius: '50%',
            '&:focus::after': {
              borderRadius: '50%',
            },
          }
        : {}),
    },
  });
});

export const usePanelStyles = (customStyles?: Partial<IPanelStyles>, bgColorOverride?: string) => {
  const { isMobileView } = usePanelMobileView();
  const theme = useAppTheme();
  const themeName = useCurrentThemeType();
  const isWebView = pageDataService.isWebView();
  return {
    styles: getPanelStyles(theme, themeName, isWebView, isMobileView, customStyles, bgColorOverride),
    classNames: getStyles(isMobileView, isWebView),
  };
};

const getPanelOverlayStyles = (
  isWebView: boolean,
  theme: IAppTheme,
  customStyles?: IStyleFunctionOrObject<IOverlayStyleProps, IOverlayStyles>
) =>
  memoizeFunction(() =>
    mergeStyleSets(
      {
        root: {
          // Note: The overlay background color sits on top of the panel background color in WebView, so we need to set it to 'transparent'.
          // On Web, we need to use white40ToBlack40 instead of dialogOverlayBackground because there is no other overlay for panels.
          backgroundColor: isWebView ? 'transparent' : theme.appColors?.white40ToBlack40,
        },
      },
      customStyles
    )
  );

export const usePanelOverlayStyles = (
  isWebView: boolean,
  customStyles?: IStyleFunctionOrObject<IOverlayStyleProps, IOverlayStyles>
): IStyleFunctionOrObject<IOverlayStyleProps, IOverlayStyles> => {
  const theme = useTheme();
  return getPanelOverlayStyles(isWebView, theme, customStyles);
};
